// Office scoping context. Mounted by routes/office-layout.jsx around every
// /offices/:officeId/* page. Loads /api/me once (TanStack Query, key ['me'])
// and derives the current office from the URL param.

import { createContext, useContext, useMemo } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api.js';

const OfficeContext = createContext(null);

/** The post-login bootstrap query — shared by _index, onboarding and the office layout. */
export function useMe() {
  return useQuery({ queryKey: ['me'], queryFn: () => apiFetch('/api/me') });
}

export function OfficeProvider({ children }) {
  const { officeId } = useParams();
  const me = useMe();

  const value = useMemo(() => {
    const offices = me.data?.offices ?? [];
    const agencies = me.data?.agencies ?? [];
    const currentOffice = offices.find((office) => office.id === officeId) ?? null;
    // Two-level nav (spec 2026-06-12) : the rail shows the CURRENT agency's
    // offices ; the switcher hops between accessible agencies.
    const currentAgency = agencies.find((agency) => agency.id === currentOffice?.agency_id) ?? null;
    const agencyOffices = currentAgency
      ? offices.filter((office) => office.agency_id === currentAgency.id)
      : [];
    return {
      user: me.data?.user ?? null,
      offices,
      agencies,
      officeId,
      currentOffice,
      currentAgency,
      agencyOffices,
      canAdminCurrentAgency: currentAgency?.can_admin === true,
      /** Platform feature flags from /api/me (`features`), not office data.
       * `legacyConnectors` = MCP_DIRECT_CONNECTORS=on : the direct-connector
       * panels render at all. Read it HERE rather than off a domain payload —
       * a rollback lever must survive that payload's fetch failing. */
      legacyConnectors: me.data?.features?.legacy_connectors === true,
      /** Stakeholder role in the current office : 'poc' | 'stakeholder' | 'viewer' | null. */
      role: currentOffice?.stakeholder_role ?? null,
      isPoc: currentOffice?.stakeholder_role === 'poc',
      status: me.status,
      error: me.error,
    };
  }, [me.data, me.status, me.error, officeId]);

  return <OfficeContext.Provider value={value}>{children}</OfficeContext.Provider>;
}

/** @returns {{ user: object|null, offices: object[], agencies: object[], officeId: string, currentOffice: object|null, currentAgency: object|null, agencyOffices: object[], canAdminCurrentAgency: boolean, legacyConnectors: boolean, role: string|null, isPoc: boolean, status: string, error: unknown }} */
export function useOffice() {
  const ctx = useContext(OfficeContext);
  if (!ctx) throw new Error('useOffice must be used inside <OfficeProvider>');
  return ctx;
}

/** Same context, or null outside an <OfficeProvider> (a card that may be mounted in isolation). */
export function useOfficeOptional() {
  return useContext(OfficeContext);
}
